import React, { useState, useEffect } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign, TrendingUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const CostBurnReport = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [costData, setCostData] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchCostBurn();
    }
  }, [projectId]);

  const fetchCostBurn = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/reports/projects/${projectId}/cost`);
      setCostData(response.data);
    } catch (error) {
      toast.error('Maliyet raporu yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!costData) {
    return (
      <div className="text-center py-12 text-gray-600">
        Maliyet verisi bulunamadı
      </div>
    );
  }

  const {
    budget,
    spent,
    remaining,
    completion_percent,
    eac,
    etc,
    cost_variance,
    cost_variance_percent,
    weekly_burn,
    burn_rate
  } = costData;

  const isOverBudget = spent > budget;
  const isOnTrack = cost_variance_percent > -10 && cost_variance_percent < 10;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Budget */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Bütçe</span>
            <DollarSign size={20} className="text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            ${budget.toLocaleString()}
          </div>
        </div>

        {/* Spent */}
        <div className={`rounded-lg shadow-sm p-6 ${isOverBudget ? 'bg-red-50' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Harcanan</span>
            {isOverBudget && <AlertTriangle size={20} className="text-red-600" />}
          </div>
          <div className={`text-2xl font-bold ${isOverBudget ? 'text-red-600' : 'text-gray-900'}`}>
            ${spent.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {completion_percent.toFixed(1)}% tamamlandı
          </div>
        </div>

        {/* EAC */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">EAC (Tahmini)</span>
            <TrendingUp size={20} className="text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            ${eac.toLocaleString()}
          </div>
          <div className={`text-xs mt-1 ${eac > budget ? 'text-red-600' : 'text-green-600'}`}>
            {eac > budget ? '+' : ''}{((eac - budget) / budget * 100).toFixed(1)}% from budget
          </div>
        </div>

        {/* Cost Variance */}
        <div className={`rounded-lg shadow-sm p-6 ${
          cost_variance < 0 ? 'bg-red-50' : cost_variance === 0 ? 'bg-gray-50' : 'bg-green-50'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Maliyet Sapması</span>
          </div>
          <div className={`text-2xl font-bold ${
            cost_variance < 0 ? 'text-red-600' : cost_variance === 0 ? 'text-gray-900' : 'text-green-600'
          }`}>
            ${Math.abs(cost_variance).toLocaleString()}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            {cost_variance_percent.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Status Indicator */}
      <div className={`rounded-lg p-4 ${
        isOverBudget ? 'bg-red-100 text-red-800' : 
        isOnTrack ? 'bg-green-100 text-green-800' : 
        'bg-yellow-100 text-yellow-800'
      }`}>
        <div className="flex items-center gap-2">
          <AlertTriangle size={20} />
          <span className="font-medium">
            {isOverBudget 
              ? 'Bütçe Aşıldı!' 
              : isOnTrack 
              ? 'Bütçe Kontrolünde' 
              : 'Dikkat: Bütçe Riski'}
          </span>
        </div>
        <p className="text-sm mt-1">
          ETC (Tamamlama için gerekli): ${etc.toLocaleString()} | 
          Burn Rate: ${burn_rate.toLocaleString()}/hafta
        </p>
      </div>

      {/* Weekly Burn Chart */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Haftalık Harcama Trendi</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={weekly_burn}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="week" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip 
              formatter={(value) => `$${value.toLocaleString()}`}
              contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="cumulative" 
              stroke="#3B82F6" 
              fill="#3B82F6" 
              fillOpacity={0.3}
              name="Kümülatif Harcama"
            />
            <Line 
              type="monotone" 
              dataKey="budget_line" 
              stroke="#EF4444" 
              strokeDasharray="5 5"
              name="Bütçe Planı"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Budget vs Spent Comparison */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bütçe vs Harcama</h3>
        <div className="space-y-4">
          {/* Budget Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Bütçe</span>
              <span className="font-medium">${budget.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-blue-600 h-4 rounded-full"
                style={{ width: '100%' }}
              ></div>
            </div>
          </div>

          {/* Spent Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Harcanan</span>
              <span className="font-medium">${spent.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className={`h-4 rounded-full ${isOverBudget ? 'bg-red-600' : 'bg-green-600'}`}
                style={{ width: `${Math.min((spent / budget) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* EAC Bar */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">EAC (Tahmini Tamamlama)</span>
              <span className="font-medium">${eac.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="bg-orange-600 h-4 rounded-full"
                style={{ width: `${Math.min((eac / budget) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostBurnReport;
